import { memo } from 'react';
import { toaster } from '@optiaxiom/react';
import { IconCalendar } from '@optimizely/axiom-icons';
import { NodeProps } from '@xyflow/react';

import {
  createScheduledTrigger,
  deleteScheduledTrigger,
  getScheduledTrigger,
  updateScheduledTrigger,
} from '../../services/scheduled-trigger';
import { getTriggerSelfUrl } from '../../types/api';
import { NodeConnectionTypes, NodeTypeCategory } from '../../types/connections';
import { NODE_TYPE } from '../../types/node';
import { defineNode } from '../../utils/node-factory';
import { BaseNodeRenderer } from '../components/renderers/BaseNodeRenderer/BaseNodeRenderer';

/**
 * Configuration for the Scheduled Trigger node type.
 * Triggers workflow execution on a schedule.
 */
export const scheduledTriggerConfig = defineNode({
  description: {
    displayName: 'Scheduled Trigger',
    name: NODE_TYPE.SCHEDULED,
    description: 'Trigger workflow execution on a schedule',
    category: NodeTypeCategory.TRIGGER,
    iconInfo: {
      icon: IconCalendar,
      color: 'fg.success',
    },
    input: {
      type: NodeConnectionTypes.Main,
      filter: undefined,
    },
    output: {
      type: NodeConnectionTypes.Main,
      displayName: 'Output',
      maxConnections: 1,
    },
    properties: [
      {
        displayName: 'Name',
        name: 'name',
        type: 'string',
        required: true,
        default: '',
        description: 'A descriptive name for this scheduled trigger',
      },
      {
        displayName: 'Description',
        name: 'description',
        type: 'string',
        required: false,
        default: '',
        description: 'Optional description of the schedule',
      },
      {
        displayName: 'Schedule Type',
        name: 'schedule_type',
        type: 'options',
        required: true,
        default: 'recurring',
        options: [
          { name: 'One-time', value: 'one_time' },
          { name: 'Recurring', value: 'recurring' },
        ],
        description: 'Type of schedule - one-time or recurring',
      },
      {
        displayName: 'Scheduled Time',
        name: 'scheduled_time',
        type: 'dateTime',
        required: false,
        default: null,
        description: 'When to execute this one-time trigger (required for one-time schedules)',
        displayOptions: {
          show: {
            schedule_type: ['one_time'],
          },
        },
      },
      {
        displayName: 'Cron Expression',
        name: 'cron_expression',
        type: 'string',
        default: null,
        required: false,
        description:
          'Cron expression defining when to trigger (e.g., "0 0 * * *" for daily at midnight, required for recurring schedules)',
        displayOptions: {
          show: {
            schedule_type: ['recurring'],
          },
        },
      },
      {
        displayName: 'Trigger Message',
        name: 'message',
        type: 'string',
        typeOptions: {
          rows: 2,
        },
        required: false,
        default: '',
        description: 'Optional chat message to send when triggering workflow execution',
      },
      {
        displayName: 'End Type',
        name: 'end_type',
        type: 'options',
        required: false,
        default: null,
        options: [
          { name: 'Max Occurrences', value: 'max_occurrences' },
          { name: 'End Date', value: 'end_date' },
        ],
        displayOptions: {
          show: {
            schedule_type: ['recurring'],
          },
        },
        description: 'Type of end condition - max occurrences or end date',
      },
      {
        displayName: 'Max Occurrences',
        name: 'max_occurrences',
        type: 'number',
        required: false,
        default: null,
        description: 'Maximum number of times to execute (optional)',
        displayOptions: {
          show: {
            schedule_type: ['recurring'],
            end_type: ['max_occurrences'],
          },
        },
      },
      {
        displayName: 'End Date',
        name: 'end_date',
        type: 'dateTime',
        required: false,
        default: null,
        description: 'Stop executing after this date (optional)',
        displayOptions: {
          show: {
            schedule_type: ['recurring'],
            end_type: ['end_date'],
          },
        },
      },
    ],
    defaults: {
      name: 'Scheduled Trigger',
      parameters: {
        name: '',
        description: '',
        schedule_type: 'recurring',
        scheduled_time: null,
        cron_expression: null,
        timezone: 'UTC',
        message: '',
        end_type: null,
        max_occurrences: null,
        end_date: null,
        is_active: true,
      },
    },
  },

  enableAsyncGet: (_nodeId: string, nodeData?: Record<string, unknown>) => {
    const selfUrl = getTriggerSelfUrl(nodeData);
    return Boolean(selfUrl && selfUrl.trim().length > 0);
  },

  onAsyncSave: async (
    _nodeId: string,
    data: Record<string, unknown>,
    context?: { instanceId?: string; workflowAgentId?: string; nodeData?: Record<string, unknown> }
  ) => {
    if (!context?.instanceId || !context?.workflowAgentId) {
      throw new Error('Instance ID or Workflow Agent ID not provided in context');
    }

    // Clean the data before sending to backend
    const cleanedData = { ...data };
    const scheduleType = cleanedData.schedule_type as string;
    const endType = cleanedData.end_type as string;

    // Handle fields based on schedule_type visibility conditions
    if (scheduleType === 'one_time') {
      cleanedData.cron_expression = null;
      cleanedData.max_occurrences = null;
      cleanedData.end_date = null;
    } else if (scheduleType === 'recurring') {
      cleanedData.scheduled_time = null;

      if (endType === 'max_occurrences') {
        cleanedData.end_date = null;
      } else if (endType === 'end_date') {
        cleanedData.max_occurrences = null;
      }
    }

    // Remove end_type since it doesn't exist in the backend
    delete cleanedData.end_type;

    const selfUrl = getTriggerSelfUrl(context.nodeData);
    if (!selfUrl) {
      const scheduledTrigger = await createScheduledTrigger(
        context.workflowAgentId,
        context.instanceId,
        cleanedData
      );
      return scheduledTrigger;
    }

    try {
      const updateData = { ...context.nodeData, ...cleanedData };
      await updateScheduledTrigger(selfUrl, updateData);
    } catch (error) {
      toaster.create('Failed to update scheduled trigger', { intent: 'danger' });
      throw error;
    }
  },

  onAsyncGet: async (
    _nodeId: string,
    context?: { instanceId?: string; workflowAgentId?: string; nodeData?: Record<string, unknown> }
  ) => {
    if (!context?.instanceId || !context?.workflowAgentId) {
      throw new Error('Instance ID or Workflow Agent ID not provided in context');
    }

    const selfUrl = getTriggerSelfUrl(context.nodeData);
    if (!selfUrl) {
      throw new Error('URL not found in node data');
    }

    try {
      const scheduledTrigger = await getScheduledTrigger(selfUrl);

      // Derive end_type from existing data since it's not stored in the backend
      if (scheduledTrigger) {
        if (scheduledTrigger.end_date != null && scheduledTrigger.end_date !== '') {
          scheduledTrigger.end_type = 'end_date';
        } else if (scheduledTrigger.max_occurrences != null) {
          scheduledTrigger.end_type = 'max_occurrences';
        } else {
          scheduledTrigger.end_type = 'max_occurrences';
        }
      }

      return scheduledTrigger;
    } catch (error) {
      if (error && typeof error === 'object' && 'status' in error && error.status === 404) {
        return {};
      }

      console.error('Failed to fetch scheduled trigger:', error);
      toaster.create('Failed to load scheduled trigger configuration', { intent: 'danger' });
      throw error;
    }
  },

  onAsyncDelete: async (
    _nodeId: string,
    context?: { instanceId?: string; workflowAgentId?: string; nodeData?: Record<string, unknown> }
  ) => {
    if (!context?.instanceId || !context?.workflowAgentId) {
      throw new Error('Instance ID or Workflow Agent ID not provided in context');
    }

    const selfUrl = getTriggerSelfUrl(context.nodeData);
    if (!selfUrl) {
      throw new Error('URL not found in node data');
    }

    try {
      await deleteScheduledTrigger(selfUrl);
      toaster.create('Scheduled trigger deleted successfully', { intent: 'success' });
    } catch (error) {
      console.error('Failed to delete scheduled trigger:', error);
      toaster.create('Failed to delete scheduled trigger', { intent: 'danger' });
      throw error;
    }
  },

  render: memo(function ScheduledTriggerRenderer(props: NodeProps) {
    return <BaseNodeRenderer {...props} />;
  }),
});
